import React, { useState } from 'react';
import { X, Save, FolderOpen, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ProjectService } from '../../services/projectService';
import { GoogleDriveService } from '../../services/googleDrive';
import { DriveFolderSearchModal } from '../DriveIntegration';
import { Project, DriveFolder } from '../../types';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectCreated: (project: Project) => void;
}

export const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  isOpen,
  onClose,
  onProjectCreated
}) => {
  const { firebaseUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active' as Project['status']
  });
  const [driveFolder, setDriveFolder] = useState<DriveFolder | null>(null);
  const [driveUrl, setDriveUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [validatingUrl, setValidatingUrl] = useState(false);
  const [error, setError] = useState('');
  const [isDriveSearchOpen, setIsDriveSearchOpen] = useState(false);

  const projectService = ProjectService.getInstance();
  const driveService = GoogleDriveService.getInstance();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firebaseUser) {
      setError('User not authenticated');
      return;
    }

    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    try {
      setSaving(true);
      setError('');

      console.log('🔄 Creating new project...');
      
      // Clean the drive folder data - only keep essential fields for Firestore
      const cleanDriveFolder = driveFolder ? {
        id: driveFolder.id,
        name: driveFolder.name,
        url: driveFolder.url
      } : undefined;

      const projectData = {
        ...formData,
        name: formData.name.trim(),
        description: formData.description.trim(),
        ownerId: firebaseUser.uid,
        driveFolder: cleanDriveFolder
      };

      const projectId = await projectService.createProject(projectData);
      
      console.log('✅ Project created with ID:', projectId);

      // Create the full project object to return
      const newProject: Project = {
        id: projectId,
        ...projectData,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      onProjectCreated(newProject);
      onClose();
      
      // Reset form
      setFormData({ name: '', description: '', status: 'active' });
      setDriveFolder(null);
      setDriveUrl('');
    } catch (err: any) {
      console.error('❌ Error creating project:', err);
      setError(`Failed to create project: ${err.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDriveUrlValidation = async () => {
    if (!driveUrl.trim()) {
      setDriveFolder(null);
      return;
    }

    if (!firebaseUser) {
      setError('User not authenticated');
      return;
    }

    try {
      setValidatingUrl(true);
      setError('');

      console.log('🔍 Validating Drive URL...');
      
      const result = await driveService.validateFolderUrl(driveUrl.trim());
      
      if (result.valid && result.folder) {
        setDriveFolder(result.folder);
        console.log('✅ Valid Drive folder:', result.folder.name);
      } else {
        setError('Invalid Google Drive folder URL. Please check the URL and ensure you have access to the folder.');
        setDriveFolder(null);
      }
    } catch (err: any) {
      console.error('❌ Error validating Drive URL:', err);
      setError(`Failed to validate Drive URL: ${err.message || 'Unknown error'}`);
      setDriveFolder(null);
    } finally {
      setValidatingUrl(false);
    }
  };

  const handleDriveUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setDriveUrl(url);
    
    // Clear previous validation if URL is cleared
    if (!url.trim()) {
      setDriveFolder(null);
      setError('');
    }
  };

  const handleDriveUrlBlur = () => {
    if (driveUrl.trim() && driveUrl !== driveFolder?.url) {
      handleDriveUrlValidation();
    }
  };

  const handleRemoveDriveFolder = () => {
    setDriveFolder(null);
    setDriveUrl('');
  };

  const handleDriveFolderSelected = (folder: DriveFolder) => {
    setDriveFolder(folder);
    setDriveUrl(folder.url);
    setError('');
    console.log('✅ Drive folder selected:', folder.name);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <FolderOpen className="h-6 w-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">Create New Project</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Project Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Project Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Enter project name"
              required
            />
          </div>

          {/* Project Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Describe your project"
            />
          </div>

          {/* Project Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
              Initial Status
            </label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as Project['status'] })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Google Drive Integration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Google Drive Folder (Optional)
            </label>
            
            {!driveFolder ? (
              <div className="space-y-3">
                <div className="flex space-x-2">
                  <div className="flex-1">
                    <input
                      type="url"
                      value={driveUrl}
                      onChange={handleDriveUrlChange}
                      onBlur={handleDriveUrlBlur}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Paste Google Drive folder URL"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsDriveSearchOpen(true)}
                    className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    title="Search Drive folders"
                  >
                    <Search className="w-5 h-5" />
                  </button>
                </div>
                
                {validatingUrl && (
                  <div className="flex items-center text-sm text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Validating folder...
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center space-x-2">
                  <FolderOpen className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="font-medium text-green-900">{driveFolder.name}</div>
                    <div className="text-sm text-green-700">Folder linked successfully</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveDriveFolder}
                  className="text-green-600 hover:text-green-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
            
            <p className="text-xs text-gray-500 mt-2">
              Link your project to a Google Drive folder for easy file access
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || validatingUrl || !formData.name.trim()}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Create Project
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Drive Folder Search Modal */}
      <DriveFolderSearchModal
        isOpen={isDriveSearchOpen}
        onClose={() => setIsDriveSearchOpen(false)}
        onFolderSelected={handleDriveFolderSelected}
      />
    </div>
  );
};
